import { useState, useRef, useEffect } from 'react';
import { LogOut, ChevronDown, Key } from 'lucide-react';
import { useAuth } from '@/lib/auth';
import { ChangePasswordModal } from '@/components/auth/ChangePasswordModal';

/**
 * Non-blocking account control. The console runs in local mode by default;
 * signing in switches it to live Supabase data (RLS-scoped to your org).
 */
export function AccountControl() {
  const { session, signOut } = useAuth();
  const [open, setOpen] = useState(false);
  const [changePasswordOpen, setChangePasswordOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const click = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', click);
    return () => document.removeEventListener('mousedown', click);
  }, []);

  if (!session) return null;

  const displayName = session.user.name || session.user.email;

  return (
    <>
      <div ref={ref} className="relative inline-block text-left">
        <button
          onClick={() => setOpen(!open)}
          className="flex items-center gap-2 rounded-xl px-3 py-1.5 hover:bg-black/5 dark:hover:bg-white/5 transition-colors neo-interactive"
          title="Account options"
        >
          <div className="flex flex-col items-end">
            <span className="text-sm font-bold text-foreground leading-tight max-w-[120px] truncate">
              {displayName.split(' ')[0]}
            </span>
            {displayName.split(' ').length > 1 && (
              <span className="text-[10px] text-muted-foreground font-medium leading-tight max-w-[120px] truncate">
                {displayName.split(' ').slice(1).join(' ')}
              </span>
            )}
          </div>
          <ChevronDown className="h-4 w-4 text-muted-foreground" />
        </button>

        {open && (
          <div className="absolute right-0 top-full mt-2 z-50 min-w-[200px] overflow-hidden rounded-xl border border-[rgba(255,255,255,0.05)] bg-[var(--color-bg)] shadow-xl neo p-1 space-y-0.5">
            <div className="px-3 py-2 border-b border-border mb-1">
              <p className="text-xs font-semibold text-foreground truncate">{session.user.name || 'User'}</p>
              <p className="text-[10px] text-muted-foreground truncate">{session.user.email}</p>
              <span className="inline-block mt-1 text-[10px] px-1.5 py-0.5 rounded bg-primary/10 text-primary font-medium">{session.user.role}</span>
            </div>

            <button
              type="button"
              onClick={() => {
                setOpen(false);
                setChangePasswordOpen(true);
              }}
              className="flex w-full items-center gap-2 rounded-lg px-3 py-2 text-left text-sm font-medium text-foreground hover:bg-black/5 dark:hover:bg-white/5 transition-colors"
            >
              <Key className="h-4 w-4 text-muted-foreground" /> Change Password
            </button>

            <button
              type="button"
              onClick={async () => {
                setOpen(false);
                await signOut();
              }}
              className="flex w-full items-center gap-2 rounded-lg px-3 py-2 text-left text-sm font-medium text-destructive hover:bg-destructive/10 transition-colors cursor-pointer"
            >
              <LogOut className="h-4 w-4" /> Sign out
            </button>
          </div>
        )}
      </div>

      <ChangePasswordModal 
        open={changePasswordOpen} 
        onClose={() => setChangePasswordOpen(false)} 
      />
    </>
  );
}

